import axiosInstance, { tokenService } from './axios'
import axios from 'axios'
import type {
  LoginRequest,
  LoginResponse,
  User,
  Employee,
  Role,
  Permission,
  Product,
  Service,
  Category,
  Unit,
  Customer,
  Supplier,
  Sale,
  SaleDetail,
  Purchase,
  PurchaseDetail,
  InventoryMovement,
  Tax,
  PaymentMethod,
  DocumentType,
  Pathology,
  Appointment,
  OpticalExam,
  OpticalFormula,
  LensMaterial,
  Filter,
  ApiResponse,
} from '../types/api'

// Auth API
export const authApi = {
  login: async (data: LoginRequest): Promise<LoginResponse> => {
    const response = await axiosInstance.post<LoginResponse>('/auth/login', data)
    return response.data
  },

  logout: async (): Promise<void> => {
    const access = tokenService.getAccessToken()
    try {
      // Intento normal: usar axiosInstance (agrega Authorization si existe)
      await axiosInstance.post('/auth/logout')
    } catch (err) {
      // Si falla (p. ej. 401 por token expirado), intentar llamar al endpoint
      // usando la instancia global de axios con el access token explícito.
      try {
        const base = axiosInstance.defaults.baseURL || ''
        const url = `${base.replace(/\/$/, '')}/auth/logout`
        // Construir config y añadir Authorization explícito si disponemos de access token
        const config: any = { headers: {} }
        if (access) {
          config.headers.Authorization = `Bearer ${access}`
        }
        await axios.post(url, {}, config)
      } catch (err2) {
        // Ignorar errores: no podemos hacer más desde el cliente.
      }
    } finally {
      tokenService.clearTokens()
    }
  },

  me: async (): Promise<User> => {
    const response = await axiosInstance.get<User>('/auth/me')
    return response.data
  },

  refresh: async (): Promise<LoginResponse> => {
    const refresh_token = tokenService.getRefreshToken()
    // Llamar con la instancia global para evitar enviar headers adicionales
    const base = axiosInstance.defaults.baseURL || ''
    const url = `${base.replace(/\/$/, '')}/auth/refresh`
    const response = await axios.post<LoginResponse>(url, { refresh_token })
    return response.data
  },
}

// Users API
export const usersApi = {
  getAll: async (): Promise<User[]> => {
    const response = await axiosInstance.get<User[]>('/users')
    return response.data // Array directo
  },

  getById: async (id: number): Promise<User> => {
    const response = await axiosInstance.get<User>(`/users/${id}`)
    // Verificar si viene envuelto en ApiResponse o directamente
    return (response.data as any).data || response.data
  },

  getByUsername: async (username: string): Promise<User> => {
    const response = await axiosInstance.get<ApiResponse<User>>(`/users/username/${username}`)
    return response.data
  },

  create: async (data: Partial<User>): Promise<User> => {
    const response = await axiosInstance.post<ApiResponse<User>>('/users', data)
    return response.data
  },

  update: async (id: number, data: Partial<User>): Promise<User> => {
    const response = await axiosInstance.put<ApiResponse<User>>(`/users/${id}`, data)
    return response.data
  },

  delete: async (id: number): Promise<void> => {
    await axiosInstance.delete(`/users/${id}`)
  },

  restore: async (id: number): Promise<User> => {
    const response = await axiosInstance.patch<ApiResponse<User>>(`/users/${id}/restore`)
    return response.data
  },
}

// Employees API
export const employeesApi = {
  getAll: async (): Promise<Employee[]> => {
    const response = await axiosInstance.get<Employee[]>('/employees')
    return response.data // Array directo
  },

  filterBy: async (column: string, value: string): Promise<Employee | Employee[]> => {
    const response = await axiosInstance.get<Employee | Employee[]>('/employees/filterBy', {
      params: { column, value },
    })
    return response.data
  },

  getById: async (id: number): Promise<Employee> => {
    const response = await axiosInstance.get<Employee>(`/employees/${id}`)
    return response.data
  },

  create: async (data: Partial<Employee>): Promise<Employee> => {
    const response = await axiosInstance.post<ApiResponse<Employee>>('/employees', data)
    return response.data
  },

  update: async (id: number, data: Partial<Employee>): Promise<Employee> => {
    const response = await axiosInstance.put<ApiResponse<Employee>>(`/employees/${id}`, data)
    return response.data
  },

  delete: async (id: number): Promise<void> => {
    await axiosInstance.delete(`/employees/${id}`)
  },

  deleteMultiple: async (ids: number[]): Promise<void> => {
    await axiosInstance.post('/employees/delete-multiple', { ids })
  },

  restore: async (id: number): Promise<Employee> => {
    const response = await axiosInstance.patch<ApiResponse<Employee>>(`/employees/${id}/restore`)
    return response.data
  },
}

// Roles API
export const rolesApi = {
  getAll: async (): Promise<Role[]> => {
    const response = await axiosInstance.get<ApiResponse<Role[]>>('/roles')
    return response.data
  },

  getById: async (id: number): Promise<Role> => {
    const response = await axiosInstance.get<ApiResponse<Role>>(`/roles/${id}`)
    return response.data
  },

  create: async (data: Partial<Role>): Promise<Role> => {
    const response = await axiosInstance.post<ApiResponse<Role>>('/roles', data)
    return response.data
  },

  update: async (id: number, data: Partial<Role>): Promise<Role> => {
    const response = await axiosInstance.put<ApiResponse<Role>>(`/roles/${id}`, data)
    return response.data
  },

  delete: async (id: number): Promise<void> => {
    await axiosInstance.delete(`/roles/${id}`)
  },

  restore: async (id: number): Promise<Role> => {
    const response = await axiosInstance.patch<ApiResponse<Role>>(`/roles/${id}/restore`)
    return response.data
  },
}

// Permissions API
export const permissionsApi = {
  getAll: async (params?: { group_by_module?: boolean; with_trashed?: boolean }): Promise<Permission[]> => {
    const response = await axiosInstance.get<ApiResponse<Permission[]>>('/permissions', { params })
    return response.data
  },

  getGrouped: async (params?: { with_trashed?: boolean }): Promise<GroupedPermission[]> => {
    const response = await axiosInstance.get<ApiResponse<GroupedPermission[]>>('/permissions', {
      params: { ...params, group_by_module: true },
    })
    return response.data
  },

  getById: async (id: number): Promise<Permission> => {
    const response = await axiosInstance.get<ApiResponse<Permission>>(`/permissions/${id}`)
    return response.data
  },

  create: async (data: Partial<Permission>): Promise<Permission> => {
    const response = await axiosInstance.post<ApiResponse<Permission>>('/permissions', data)
    return response.data
  },

  update: async (id: number, data: Partial<Permission>): Promise<Permission> => {
    const response = await axiosInstance.put<ApiResponse<Permission>>(`/permissions/${id}`, data)
    return response.data
  },

  delete: async (id: number): Promise<void> => {
    await axiosInstance.delete(`/permissions/${id}`)
  },

  restore: async (id: number): Promise<Permission> => {
    const response = await axiosInstance.patch<ApiResponse<Permission>>(`/permissions/${id}/restore`)
    return response.data
  },
}

// Products API
export const productsApi = {
  getAll: async (): Promise<Product[]> => {
    const response = await axiosInstance.get<Product[]>('/products')
    return response.data // Array directo
  },

  getById: async (id: number): Promise<Product> => {
    const response = await axiosInstance.get<Product>(`/products/${id}`)
    return response.data // Objeto directo
  },

  create: async (data: Partial<Product>): Promise<Product> => {
    const response = await axiosInstance.post<ApiResponse<Product>>('/products', data)
    return response.data
  },

  update: async (id: number, data: Partial<Product>): Promise<Product> => {
    const response = await axiosInstance.put<ApiResponse<Product>>(`/products/${id}`, data)
    return response.data
  },

  delete: async (id: number): Promise<void> => {
    await axiosInstance.delete(`/products/${id}`)
  },

  restore: async (id: number): Promise<Product> => {
    const response = await axiosInstance.patch<ApiResponse<Product>>(`/products/${id}/restore`)
    return response.data // Wrapped
  },
}

// Services API
export const servicesApi = {
  getAll: async (): Promise<Service[]> => {
    const response = await axiosInstance.get<Service[]>('/services')
    return response.data // Array directo
  },

  getById: async (id: number): Promise<Service> => {
    const response = await axiosInstance.get<Service>(`/services/${id}`)
    return response.data // Objeto directo
  },

  create: async (data: Partial<Service>): Promise<Service> => {
    const response = await axiosInstance.post<ApiResponse<Service>>('/services', data)
    return response.data
  },

  update: async (id: number, data: Partial<Service>): Promise<Service> => {
    const response = await axiosInstance.put<ApiResponse<Service>>(`/services/${id}`, data)
    return response.data
  },

  delete: async (id: number): Promise<void> => {
    await axiosInstance.delete(`/services/${id}`)
  },

  restore: async (id: number): Promise<Service> => {
    const response = await axiosInstance.patch<ApiResponse<Service>>(`/services/${id}/restore`)
    return response.data // Wrapped
  },
}

// Categories API
export const categoriesApi = {
  getAll: async (): Promise<Category[]> => {
    const response = await axiosInstance.get<Category[]>('/categories')
    return response.data
  },

  getParents: async (): Promise<Category[]> => {
    const response = await axiosInstance.get<Category[]>('/categories/parents')
    return response.data
  },

  getChildren: async (id: number): Promise<Category[]> => {
    const response = await axiosInstance.get<Category[]>(`/categories/${id}/children`)
    return response.data
  },

  getById: async (id: number): Promise<Category> => {
    const response = await axiosInstance.get<Category>(`/categories/${id}`)
    return response.data
  },

  create: async (data: Partial<Category>): Promise<Category> => {
    const response = await axiosInstance.post<ApiResponse<Category>>('/categories', data)
    return response.data
  },

  update: async (id: number, data: Partial<Category>): Promise<Category> => {
    const response = await axiosInstance.put<ApiResponse<Category>>(`/categories/${id}`, data)
    return response.data
  },

  delete: async (id: number): Promise<void> => {
    await axiosInstance.delete(`/categories/${id}`)
  },

  restore: async (id: number): Promise<Category> => {
    const response = await axiosInstance.patch<ApiResponse<Category>>(`/categories/${id}/restore`)
    return response.data
  },
}

// Units API
export const unitsApi = {
  getAll: async (): Promise<Unit[]> => {
    const response = await axiosInstance.get<Unit[]>('/units')
    return response.data
  },

  getBase: async (): Promise<Unit[]> => {
    const response = await axiosInstance.get<Unit[]>('/units/base')
    return response.data
  },

  getById: async (id: number): Promise<Unit> => {
    const response = await axiosInstance.get<Unit>(`/units/${id}`)
    return response.data
  },

  create: async (data: Partial<Unit>): Promise<Unit> => {
    const response = await axiosInstance.post<ApiResponse<Unit>>('/units', data)
    return response.data
  },

  update: async (id: number, data: Partial<Unit>): Promise<Unit> => {
    const response = await axiosInstance.put<ApiResponse<Unit>>(`/units/${id}`, data)
    return response.data
  },

  delete: async (id: number): Promise<void> => {
    await axiosInstance.delete(`/units/${id}`)
  },

  restore: async (id: number): Promise<Unit> => {
    const response = await axiosInstance.patch<ApiResponse<Unit>>(`/units/${id}/restore`)
    return response.data
  },
}

// Customers API
export const customersApi = {
  getAll: async (): Promise<Customer[]> => {
    const response = await axiosInstance.get<Customer[]>('/customers', {
      params: {
        include: 'document_type',
      },
    })
    return response.data // Array directo
  },

  getById: async (id: number): Promise<Customer> => {
    const response = await axiosInstance.get<Customer>(`/customers/${id}`, {
      params: {
        include: 'document_type',
      },
    })
    return response.data
  },

  create: async (data: Partial<Customer>): Promise<Customer> => {
    const response = await axiosInstance.post<ApiResponse<Customer>>('/customers', data)
    return response.data
  },

  update: async (id: number, data: Partial<Customer>): Promise<Customer> => {
    const response = await axiosInstance.put<ApiResponse<Customer>>(`/customers/${id}`, data)
    return response.data
  },

  delete: async (id: number): Promise<void> => {
    await axiosInstance.delete(`/customers/${id}`)
  },

  restore: async (id: number): Promise<Customer> => {
    const response = await axiosInstance.patch<ApiResponse<Customer>>(`/customers/${id}/restore`)
    return response.data
  },
}

// Suppliers API
export const suppliersApi = {
  getAll: async (): Promise<Supplier[]> => {
    const response = await axiosInstance.get<Supplier[]>('/suppliers')
    return response.data // Array directo
  },

  getById: async (id: number): Promise<Supplier> => {
    const response = await axiosInstance.get<ApiResponse<Supplier>>(`/suppliers/${id}`)
    return response.data
  },

  create: async (data: Partial<Supplier>): Promise<Supplier> => {
    const response = await axiosInstance.post<ApiResponse<Supplier>>('/suppliers', data)
    return response.data
  },

  update: async (id: number, data: Partial<Supplier>): Promise<Supplier> => {
    const response = await axiosInstance.put<ApiResponse<Supplier>>(`/suppliers/${id}`, data)
    return response.data
  },

  delete: async (id: number): Promise<void> => {
    await axiosInstance.delete(`/suppliers/${id}`)
  },

  restore: async (id: number): Promise<Supplier> => {
    const response = await axiosInstance.patch<ApiResponse<Supplier>>(`/suppliers/${id}/restore`)
    return response.data
  },
}

// Sales API
export const salesApi = {
  getAll: async (): Promise<Sale[]> => {
    const response = await axiosInstance.get<Sale[]>('/sales')
    return response.data // Array directo
  },

  getById: async (id: number): Promise<Sale> => {
    const response = await axiosInstance.get<Sale>(`/sales/${id}`)
    return response.data // Objeto directo
  },

  create: async (data: Partial<Sale>): Promise<Sale> => {
    const response = await axiosInstance.post<ApiResponse<Sale>>('/sales', data)
    return response.data.data // Wrapped
  },

  update: async (id: number, data: Partial<Sale>): Promise<Sale> => {
    const response = await axiosInstance.put<ApiResponse<Sale>>(`/sales/${id}`, data)
    return response.data.data // Wrapped
  },

  delete: async (id: number): Promise<void> => {
    await axiosInstance.delete(`/sales/${id}`)
  },

  restore: async (id: number): Promise<Sale> => {
    const response = await axiosInstance.patch<ApiResponse<Sale>>(`/sales/${id}/restore`)
    return response.data.data // Wrapped
  },

  cancel: async (id: number, data: { cancellation_note: string }): Promise<Sale> => {
    const response = await axiosInstance.patch<ApiResponse<Sale>>(`/sales/${id}/cancel`, data)
    return response.data.data // Wrapped
  },

  downloadInvoicePdf: async (id: number): Promise<Blob> => {
    const response = await axiosInstance.get(`/sales/${id}/invoice`, {
      responseType: 'blob',
    })
    return response.data
  },
}

// Purchases API
export const purchasesApi = {
  getAll: async (): Promise<Purchase[]> => {
    const response = await axiosInstance.get<Purchase[]>('/purchases')
    return response.data // Array directo
  },

  getById: async (id: number): Promise<Purchase> => {
    const response = await axiosInstance.get<Purchase>(`/purchases/${id}`)
    return response.data // Objeto directo
  },

  create: async (data: Partial<Purchase>): Promise<Purchase> => {
    const response = await axiosInstance.post<ApiResponse<Purchase>>('/purchases', data)
    return response.data // Wrapped
  },

  update: async (id: number, data: Partial<Purchase>): Promise<Purchase> => {
    const response = await axiosInstance.put<ApiResponse<Purchase>>(`/purchases/${id}`, data)
    return response.data // Wrapped
  },

  delete: async (id: number): Promise<void> => {
    await axiosInstance.delete(`/purchases/${id}`)
  },

  restore: async (id: number): Promise<Purchase> => {
    const response = await axiosInstance.patch<ApiResponse<Purchase>>(`/purchases/${id}/restore`)
    return response.data // Wrapped
  },

  cancel: async (id: number): Promise<Purchase> => {
    const response = await axiosInstance.patch<ApiResponse<Purchase>>(`/purchases/${id}/cancel`)
    return response.data // Wrapped
  },
}

// Inventory Movements API
export const inventoryMovementsApi = {
  getAll: async (): Promise<InventoryMovement[]> => {
    const response = await axiosInstance.get<InventoryMovement[]>('/inventory-movements')
    return response.data // Array directo
  },

  getById: async (id: number): Promise<InventoryMovement> => {
    const response = await axiosInstance.get<InventoryMovement>(`/inventory-movements/${id}`)
    return response.data
  },

  getByProductId: async (productId: number): Promise<InventoryMovement[]> => {
    const response = await axiosInstance.get<InventoryMovement[]>(`/inventory-movements/product/${productId}`)
    return response.data
  },

  create: async (data: Partial<InventoryMovement>): Promise<InventoryMovement> => {
    const response = await axiosInstance.post<ApiResponse<InventoryMovement>>('/inventory-movements', data)
    return response.data.data
  },

  update: async (id: number, data: Partial<InventoryMovement>): Promise<InventoryMovement> => {
    const response = await axiosInstance.put<ApiResponse<InventoryMovement>>(`/inventory-movements/${id}`, data)
    return response.data.data
  },

  delete: async (id: number): Promise<void> => {
    await axiosInstance.delete(`/inventory-movements/${id}`)
  },

  restore: async (id: number): Promise<InventoryMovement> => {
    const response = await axiosInstance.post<ApiResponse<InventoryMovement>>(`/inventory-movements/${id}/restore`)
    return response.data.data
  },
}

// Taxes API
export const taxesApi = {
  getAll: async (): Promise<Tax[]> => {
    const response = await axiosInstance.get<Tax[]>('/taxes')
    return response.data
  },

  getById: async (id: number): Promise<Tax> => {
    const response = await axiosInstance.get<Tax>(`/taxes/${id}`)
    return response.data
  },

  create: async (data: Partial<Tax>): Promise<Tax> => {
    const response = await axiosInstance.post<ApiResponse<Tax>>('/taxes', data)
    return response.data
  },

  update: async (id: number, data: Partial<Tax>): Promise<Tax> => {
    const response = await axiosInstance.put<ApiResponse<Tax>>(`/taxes/${id}`, data)
    return response.data
  },

  delete: async (id: number): Promise<void> => {
    await axiosInstance.delete(`/taxes/${id}`)
  },

  restore: async (id: number): Promise<Tax> => {
    const response = await axiosInstance.patch<ApiResponse<Tax>>(`/taxes/${id}/restore`)
    return response.data
  },
}

// Payment Methods API
export const paymentMethodsApi = {
  getAll: async (): Promise<PaymentMethod[]> => {
    const response = await axiosInstance.get<ApiResponse<PaymentMethod[]>>('/payment-methods')
    return response.data
  },

  getActive: async (): Promise<PaymentMethod[]> => {
    const response = await axiosInstance.get<ApiResponse<PaymentMethod[]>>('/payment-methods/active')
    return response.data
  },

  getById: async (id: number): Promise<PaymentMethod> => {
    const response = await axiosInstance.get<ApiResponse<PaymentMethod>>(`/payment-methods/${id}`)
    return response.data
  },

  create: async (data: Partial<PaymentMethod>): Promise<PaymentMethod> => {
    const response = await axiosInstance.post<ApiResponse<PaymentMethod>>('/payment-methods', data)
    return response.data
  },

  update: async (id: number, data: Partial<PaymentMethod>): Promise<PaymentMethod> => {
    const response = await axiosInstance.put<ApiResponse<PaymentMethod>>(`/payment-methods/${id}`, data)
    return response.data
  },

  delete: async (id: number): Promise<void> => {
    await axiosInstance.delete(`/payment-methods/${id}`)
  },
}

// Document Types API
export const documentTypesApi = {
  getAll: async (): Promise<DocumentType[]> => {
    const response = await axiosInstance.get<DocumentType[]>('/document-types')
    return response.data
  },

  getActive: async (): Promise<DocumentType[]> => {
    const response = await axiosInstance.get<DocumentType[]>('/document-types/active')
    return response.data
  },

  getById: async (id: number): Promise<DocumentType> => {
    const response = await axiosInstance.get<ApiResponse<DocumentType>>(`/document-types/${id}`)
    return response.data
  },

  create: async (data: Partial<DocumentType>): Promise<DocumentType> => {
    const response = await axiosInstance.post<ApiResponse<DocumentType>>('/document-types', data)
    return response.data
  },

  update: async (id: number, data: Partial<DocumentType>): Promise<DocumentType> => {
    const response = await axiosInstance.put<ApiResponse<DocumentType>>(`/document-types/${id}`, data)
    return response.data
  },

  delete: async (id: number): Promise<void> => {
    await axiosInstance.delete(`/document-types/${id}`)
  },
}

// Purchase Details API
export const purchaseDetailsApi = {
  getAll: async (): Promise<PurchaseDetail[]> => {
    const response = await axiosInstance.get<PurchaseDetail[]>('/purchase-details')
    return response.data // Array directo
  },

  getById: async (id: number): Promise<PurchaseDetail> => {
    const response = await axiosInstance.get<ApiResponse<PurchaseDetail>>(`/purchase-details/${id}`)
    return response.data
  },

  create: async (data: Partial<PurchaseDetail>): Promise<PurchaseDetail> => {
    const response = await axiosInstance.post<ApiResponse<PurchaseDetail>>('/purchase-details', data)
    return response.data
  },

  update: async (id: number, data: Partial<PurchaseDetail>): Promise<PurchaseDetail> => {
    const response = await axiosInstance.put<ApiResponse<PurchaseDetail>>(`/purchase-details/${id}`, data)
    return response.data
  },

  delete: async (id: number): Promise<void> => {
    await axiosInstance.delete(`/purchase-details/${id}`)
  },

  restore: async (id: number): Promise<PurchaseDetail> => {
    const response = await axiosInstance.patch<ApiResponse<PurchaseDetail>>(`/purchase-details/${id}/restore`)
    return response.data
  },
}

// Sale Details API
export const saleDetailsApi = {
  getAll: async (): Promise<SaleDetail[]> => {
    const response = await axiosInstance.get<SaleDetail[]>('/sale-details')
    return response.data // Array directo
  },

  getById: async (id: number): Promise<SaleDetail> => {
    const response = await axiosInstance.get<ApiResponse<SaleDetail>>(`/sale-details/${id}`)
    return response.data
  },

  create: async (data: Partial<SaleDetail>): Promise<SaleDetail> => {
    const response = await axiosInstance.post<ApiResponse<SaleDetail>>('/sale-details', data)
    return response.data
  },

  update: async (id: number, data: Partial<SaleDetail>): Promise<SaleDetail> => {
    const response = await axiosInstance.put<ApiResponse<SaleDetail>>(`/sale-details/${id}`, data)
    return response.data
  },

  delete: async (id: number): Promise<void> => {
    await axiosInstance.delete(`/sale-details/${id}`)
  },

  restore: async (id: number): Promise<SaleDetail> => {
    const response = await axiosInstance.patch<ApiResponse<SaleDetail>>(`/sale-details/${id}/restore`)
    return response.data
  },
}

// Optical Module API - Pathologies
export const pathologiesApi = {
  getAll: async (params?: any): Promise<Pathology[]> => {
    const response = await axiosInstance.get<any>('/pathologies', { params })
    // Si la respuesta es un array directo, devolverlo tal cual
    if (Array.isArray(response.data)) {
      return response.data
    }
    // Si es paginada (tiene propiedad 'data'), extraer el array
    if (response.data && typeof response.data === 'object' && 'data' in response.data) {
      return response.data.data
    }
    return []
  },

  getById: async (id: number): Promise<Pathology> => {
    const response = await axiosInstance.get<Pathology>(`/pathologies/${id}`)
    return response.data
  },

  create: async (data: Partial<Pathology>): Promise<Pathology> => {
    const response = await axiosInstance.post<ApiResponse<Pathology>>('/pathologies', data)
    return response.data.data
  },

  update: async (id: number, data: Partial<Pathology>): Promise<Pathology> => {
    const response = await axiosInstance.put<ApiResponse<Pathology>>(`/pathologies/${id}`, data)
    return response.data.data
  },

  delete: async (id: number): Promise<void> => {
    await axiosInstance.delete(`/pathologies/${id}`)
  },

  restore: async (id: number): Promise<Pathology> => {
    const response = await axiosInstance.patch<ApiResponse<Pathology>>(`/pathologies/${id}/restore`)
    return response.data.data
  },
}

// Optical Module API - Appointments
export const appointmentsApi = {
  getAll: async (params?: any): Promise<Appointment[]> => {
    const response = await axiosInstance.get<Appointment[]>('/appointments', { params })
    return response.data
  },

  getById: async (id: number): Promise<Appointment> => {
    const response = await axiosInstance.get<Appointment>(`/appointments/${id}`)
    return response.data
  },

  create: async (data: Partial<Appointment>): Promise<Appointment> => {
    const response = await axiosInstance.post<ApiResponse<Appointment>>('/appointments', data)
    return response.data.data
  },

  update: async (id: number, data: Partial<Appointment>): Promise<Appointment> => {
    const response = await axiosInstance.put<ApiResponse<Appointment>>(`/appointments/${id}`, data)
    return response.data.data
  },

  delete: async (id: number): Promise<void> => {
    await axiosInstance.delete(`/appointments/${id}`)
  },

  confirm: async (id: number): Promise<Appointment> => {
    const response = await axiosInstance.patch<ApiResponse<Appointment>>(`/appointments/${id}/confirm`)
    return response.data.data
  },

  restore: async (id: number): Promise<Appointment> => {
    const response = await axiosInstance.patch<ApiResponse<Appointment>>(`/appointments/${id}/restore`)
    return response.data.data
  },
}

// Optical Module API - Optical Exams
export const opticalExamsApi = {
  getAll: async (params?: any): Promise<OpticalExam[]> => {
    const response = await axiosInstance.get<OpticalExam[]>('/optical-exams', { params })
    return response.data
  },

  getById: async (id: number): Promise<OpticalExam> => {
    const response = await axiosInstance.get<OpticalExam>(`/optical-exams/${id}`)
    return response.data
  },

  create: async (data: Partial<OpticalExam> & { pathologies?: any[] }): Promise<OpticalExam> => {
    const response = await axiosInstance.post<ApiResponse<OpticalExam>>('/optical-exams', data)

    return response.data.data
  },

  update: async (id: number, data: Partial<OpticalExam> & { pathologies?: any[] }): Promise<OpticalExam> => {
    // Usar PUT para actualizar examen con patologías
    const response = await axiosInstance.put<ApiResponse<OpticalExam>>(`/optical-exams/${id}`, data)
    return response.data.data
  },

  deletePathology: async (examId: number, pathologyId: number): Promise<void> => {
    await axiosInstance.delete(`/exams/${examId}/pathologies/${pathologyId}`)
  },

  delete: async (id: number): Promise<void> => {
    await axiosInstance.delete(`/optical-exams/${id}`)
  },

  restore: async (id: number): Promise<OpticalExam> => {
    const response = await axiosInstance.patch<ApiResponse<OpticalExam>>(`/optical-exams/${id}/restore`)
    return response.data.data
  },

  getPatientHistory: async (customerId: number): Promise<OpticalExam[]> => {
    const response = await axiosInstance.get<OpticalExam[]>(`/optical-exams/patient/${customerId}/history`)
    return response.data
  },

  downloadClinicHistory: async (
    customerId: number,
    documentType: 'clinical-history' | 'visual-certificate' = 'clinical-history',
  ): Promise<Blob> => {
    const response = await axiosInstance.get(
      `/optical-exams/customer/${customerId}/download-clinic-history/${documentType}`,
      {
        responseType: 'blob',
      },
    )
    return response.data
  },
}

// Optical Module API - Lens Materials
export const lensMaterialsApi = {
  getAll: async (params?: any): Promise<LensMaterial[]> => {
    const response = await axiosInstance.get<LensMaterial[]>('/lens-materials', { params })
    return response.data
  },

  getById: async (id: number): Promise<LensMaterial> => {
    const response = await axiosInstance.get<LensMaterial>(`/lens-materials/${id}`)
    return response.data
  },
}

// Optical Module API - Filters
export const filtersApi = {
  getAll: async (params?: any): Promise<Filter[]> => {
    const response = await axiosInstance.get<Filter[]>('/filters', { params })
    return response.data
  },

  getById: async (id: number): Promise<Filter> => {
    const response = await axiosInstance.get<Filter>(`/filters/${id}`)
    return response.data
  },
}

// Optical Module API - Optical Formulas
export const opticalFormulasApi = {
  getAll: async (params?: any): Promise<OpticalFormula[]> => {
    const response = await axiosInstance.get<OpticalFormula[]>('/optical-formulas', { params })
    return response.data
  },

  getById: async (id: number): Promise<OpticalFormula> => {
    const response = await axiosInstance.get<OpticalFormula>(`/optical-formulas/${id}`)
    return response.data
  },

  create: async (data: Partial<OpticalFormula>): Promise<OpticalFormula> => {
    const response = await axiosInstance.post<ApiResponse<OpticalFormula>>('/optical-formulas', data)
    return response.data.data
  },

  update: async (id: number, data: Partial<OpticalFormula>): Promise<OpticalFormula> => {
    const response = await axiosInstance.put<ApiResponse<OpticalFormula>>(`/optical-formulas/${id}`, data)
    return response.data.data
  },

  delete: async (id: number): Promise<void> => {
    await axiosInstance.delete(`/optical-formulas/${id}`)
  },

  restore: async (id: number): Promise<OpticalFormula> => {
    const response = await axiosInstance.patch<ApiResponse<OpticalFormula>>(`/optical-formulas/${id}/restore`)
    return response.data.data
  },

  generatePdf: async (id: number): Promise<any> => {
    const response = await axiosInstance.post<ApiResponse<any>>(`/optical-formulas/${id}/generate-pdf`)
    return response.data.data
  },

  downloadPdf: async (id: number): Promise<Blob> => {
    const response = await axiosInstance.get(`/optical-formulas/${id}/download-pdf`, {
      responseType: 'blob',
    })
    return response.data
  },
}

// Export all APIs as a single object
export default {
  auth: authApi,
  users: usersApi,
  employees: employeesApi,
  roles: rolesApi,
  permissions: permissionsApi,
  products: productsApi,
  services: servicesApi,
  categories: categoriesApi,
  units: unitsApi,
  customers: customersApi,
  suppliers: suppliersApi,
  sales: salesApi,
  saleDetails: saleDetailsApi,
  purchases: purchasesApi,
  purchaseDetails: purchaseDetailsApi,
  inventoryMovements: inventoryMovementsApi,
  taxes: taxesApi,
  paymentMethods: paymentMethodsApi,
  documentTypes: documentTypesApi,
  pathologies: pathologiesApi,
  appointments: appointmentsApi,
  opticalExams: opticalExamsApi,
  lensMaterials: lensMaterialsApi,
  filters: filtersApi,
  opticalFormulas: opticalFormulasApi,
}
